import React from "react";
import { Box, Typography } from "@mui/material";
import { formatJPY } from "@/utils/formatJPY";
import {
  REMOTE_AREA_FEE,
  VEHICLE_REQUIRED_FEE,
} from "../constants/priceMaster";
import { type ResearchAccess } from "../types";
import {
  buildAccessDisclaimer,
  formatAccessStation,
} from "../utils/accessText";
import { HelpText, SectionCard } from "./formLayout";

interface AccessSurchargeSectionProps {
  access: ResearchAccess;
  /** 今の地番・対象住所での判定を問い合わせ中 */
  loading: boolean;
  /** 入力の済んだ地番がある（無ければ判定を問い合わせない） */
  hasChiban: boolean;
}

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "space-between",
      alignItems: "baseline",
      columnGap: 2,
      py: 0.75,
      borderBottom: "1px solid #e9ecef",
      "&:last-of-type": { borderBottom: "none" },
    }}
  >
    <Typography variant="body2" sx={{ color: "#64748b" }}>
      {label}
    </Typography>
    {/* 狭い画面では値を次の行へ送り、長い駅名も枠の中で折り返す */}
    <Typography
      variant="body2"
      fontWeight={600}
      sx={{
        ml: "auto",
        minWidth: 0,
        textAlign: "right",
        overflowWrap: "anywhere",
      }}
    >
      {value}
    </Typography>
  </Box>
);

const feeText = (applied: boolean, fee: number): string =>
  applied ? `+${formatJPY(fee)}` : "なし";

/**
 * 最寄駅からの徒歩分で自動で決まる、僻地加算と車両必須加算を出す（入力欄ではなく、判定の結果だけを並べる）。
 * 簡易調査・詳細調査を選んだときに、物件の規模の下に置く。
 */
export const AccessSurchargeSection: React.FC<AccessSurchargeSectionProps> = ({
  access,
  loading,
  hasChiban,
}) => {
  const station = !hasChiban
    ? "地番を入力すると判定します"
    : loading
    ? "確認しています…"
    : access.judged
    ? formatAccessStation(access)
    : "判定できませんでした";
  const judged = hasChiban && !loading && access.judged;

  return (
    <SectionCard title="最寄駅と加算（自動で判定）">
      {/* 判定中から結果に変わったことを読み上げでも伝える */}
      <Box
        aria-live="polite"
        sx={{ px: 1.5, backgroundColor: "white", borderRadius: 2 }}
      >
        <Row label="最寄駅" value={station} />
        {judged ? (
          <>
            <Row
              label="僻地加算（徒歩15分以上）"
              value={feeText(access.remoteArea, REMOTE_AREA_FEE)}
            />
            <Row
              label="車両必須加算（徒歩30分以上）"
              value={feeText(access.vehicleRequired, VEHICLE_REQUIRED_FEE)}
            />
          </>
        ) : null}
      </Box>
      {judged ? (
        <HelpText sx={{ mt: 1 }}>{buildAccessDisclaimer(access)}</HelpText>
      ) : null}
      {hasChiban && !loading && !access.judged ? (
        <HelpText sx={{ mt: 1 }}>
          僻地加算・車両必須加算は、運営が確認したあとにご案内します。
        </HelpText>
      ) : null}
    </SectionCard>
  );
};
